'use client';

import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { createContext, useContext, ReactNode } from 'react';

export interface FirebaseContextValue {
  app: FirebaseApp | null;
  auth: Auth | null;
  firestore: Firestore | null;
}

export const FirebaseContext = createContext<FirebaseContextValue | null>(null);

interface FirebaseProviderProps {
  children: ReactNode;
  value: FirebaseContextValue;
}

export function FirebaseProvider({ children, value }: FirebaseProviderProps) {
  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

export function useFirebase(): FirebaseContextValue {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
}

export function useFirebaseApp(): FirebaseApp {
  const { app } = useFirebase();
  if (!app) {
    throw new Error('Firebase App has not been provided');
  }
  return app;
}

export function useAuth(): Auth {
  const { auth } = useFirebase();
  if (!auth) {
    throw new Error('Firebase Auth has not been provided');
  }
  return auth;
}

export function useFirestore(): Firestore {
  const { firestore } = useFirebase();
  if (!firestore) {
    throw new Error('Firestore has not been provided');
  }
  return firestore;
}
